import React, { FC, useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { newPaginationInput, User } from "src/api/types";
import GlobalContext from "src/context/global-context";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
} from "src/lib/constants";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import {
  fixAfterDecimal,
  getCurrencyInfo,
  getErrorMessage,
  logger,
} from "src/lib/utils";
import client from "../../api";
import { Table, Button, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import { CurrencyDataType } from "src/lib/constants/table-type";

type UserBalancesPageProps = {
  user: User;
};

type SyncBalanceItem = {
  id: number;
  operation: string;
  user_id: number;
  asset_id: number;
  card_id: string;
  amount: string;
  currency: string;
  our_balance_before: string;
  bank_balance_before: string;
  our_balance_after: string;
  bank_balance_after: string;
  mismatch_delta: string | null;
  status: string;
  result_code: string;
  result_message: string;
  bank_reference: string;
  reference_type?: string | null;
  reference_id?: string | number | null;
  rrn: string;
  stan: string;
  created_at: string;
  updated_at: string;
};

const UserSyncCardBalances: FC<UserBalancesPageProps> = ({ user }) => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [dataSource, setDataSource] = useState<SyncBalanceItem[]>([]);
  const [searchParams, setSearchParams] = useState<newPaginationInput>({
    limit: LIST_PAGE_SIZE,
    page: 1,
  });
  const [currencies, setCurrencies] = useState<CurrencyDataType[]>([]);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [total, setTotal] = useState(0);

  const handleError = (error: any) => {
    if (error.code === "ERR_NETWORK") {
      setAlertText(NetworkDisconnectMsg);
    } else {
      if (error.response) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
    }

    setIsAlertOpened(true);
  };

  const { mutate: getCurrencies } = useMutation(client.currencies.all, {
    onSuccess: (data) => {
      const formattedCurrencies = data.map((item) => ({
        key: item.id,
        ...item,
      }));

      setCurrencies(formattedCurrencies);

      const defaultGelCurrency = formattedCurrencies.find(
        (item) => item.code?.toUpperCase() === "GEL",
      );

      if (defaultGelCurrency) {
        setSelectedAssetId(defaultGelCurrency.id);
      }
    },
    onError: (error: any) => {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
      }
      setIsAlertOpened(true);
    },
  });

  const { mutate: getSyncList, isLoading } = useMutation(
    client.userVirtualCards.userCardBalanceSyncList,
    {
      onSuccess: (data) => {
        setDataSource(data?.items || []);
        setTotal(data?.totalcount || 0);
      },
      onError: handleError,
    },
  );

  const { mutate: syncBankToFastoo, isLoading: isSyncBankToFastooLoading } =
    useMutation(client.userVirtualCards.syncBankToFastoo, {
      onSuccess: () => {
        getSyncList({
          userId: user.id,
          page: searchParams.page,
          limit: searchParams.limit,
        });
      },
      onError: handleError,
    });

  const {
    mutate: syncFastooWalletToBank,
    isLoading: isSyncFastooWalletToBankLoading,
  } = useMutation(client.userVirtualCards.syncFastooToBank, {
    onSuccess: () => {
      getSyncList({
        userId: user.id,
        page: searchParams.page,
        limit: searchParams.limit,
      });
    },
    onError: handleError,
  });

  const onPageChange = (page: number, pageSize: number) => {
    logger.log(page, pageSize);

    setSearchParams({
      page,
      limit: pageSize,
    });
  };

  useEffect(() => {
    if (isMounted) {
      getCurrencies();
      getSyncList({
        userId: user.id,
        page: searchParams.page,
        limit: searchParams.limit,
      });
    }
  }, [isMounted, searchParams.page, searchParams.limit]);

  const columns: ColumnsType<SyncBalanceItem> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Operation
        </p>
      ),
      dataIndex: "operation",
      key: "operation",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Currency
        </p>
      ),
      dataIndex: "currency",
      key: "currency",
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <div className="w-[24px] h-[24px]">{getCurrencyInfo(value).icon}</div>

          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value?.toUpperCase()}
          </p>
        </div>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Amount
        </p>
      ),
      dataIndex: "amount",
      key: "amount",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {fixAfterDecimal(Number(value), 2)}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Our Balance Before
        </p>
      ),
      dataIndex: "our_balance_before",
      key: "our_balance_before",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {fixAfterDecimal(Number(value), 2)}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Bank Balance Before
        </p>
      ),
      dataIndex: "bank_balance_before",
      key: "bank_balance_before",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {fixAfterDecimal(Number(value), 2)}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Our Balance After
        </p>
      ),
      dataIndex: "our_balance_after",
      key: "our_balance_after",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {fixAfterDecimal(Number(value), 2)}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Bank Balance After
        </p>
      ),
      dataIndex: "bank_balance_after",
      key: "bank_balance_after",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {fixAfterDecimal(Number(value), 2)}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Status
        </p>
      ),
      dataIndex: "status",
      key: "status",
      render: (value: string) => (
        <span
          className={`px-2 py-1 rounded text-[12px] font-medium ${
            value === "COMPLETED"
              ? "bg-green-100 text-green-600"
              : "bg-yellow-100 text-yellow-600"
          }`}
        >
          {value}
        </span>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Result Message
        </p>
      ),
      dataIndex: "result_message",
      key: "result_message",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Bank Reference
        </p>
      ),
      dataIndex: "bank_reference",
      key: "bank_reference",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value || "__"}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Reference Type
        </p>
      ),
      dataIndex: "reference_type",
      key: "reference_type",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value || "__"}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Reference ID
        </p>
      ),
      dataIndex: "reference_id",
      key: "reference_id",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value || "__"}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created At
        </p>
      ),
      dataIndex: "created_at",
      key: "created_at",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {new Date(value).toLocaleString()}
        </p>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-[12px] w-full">
      <div className="p-4">
        {/* Top Actions */}
        <div className="flex justify-between items-center gap-3 mb-4 flex-wrap">
          <div className="min-w-[180px]">
            <Select
              placeholder="Select Currency"
              value={selectedAssetId}
              onChange={(value: number) => setSelectedAssetId(value)}
              className="w-full"
              options={currencies.map((currency) => ({
                label: `${currency.code?.toUpperCase()}`,
                value: currency.id,
              }))}
            />
          </div>

          <div className="flex justify-end gap-3 flex-wrap">
            <Button
              loading={isSyncFastooWalletToBankLoading}
              disabled={!selectedAssetId}
              onClick={() =>
                syncFastooWalletToBank({
                  userId: user.id,
                  assetId: selectedAssetId,
                })
              }
              className="!bg-[#1677ff] !text-white hover:!bg-[#4096ff] !border-[#1677ff] !h-[40px] !font-medium"
            >
              Sync fastoo wallet balance to bank
            </Button>

            <Button
              loading={isSyncBankToFastooLoading}
              disabled={!selectedAssetId}
              onClick={() =>
                syncBankToFastoo({
                  userId: user.id,
                  assetId: selectedAssetId,
                })
              }
              className="!bg-[#52c41a] !text-white hover:!bg-[#73d13d] !border-[#52c41a] !h-[40px] !font-medium"
            >
              Sync bank balance to fastoo wallet
            </Button>
          </div>
        </div>

        <Table
          rowKey={(record: SyncBalanceItem) => record.id}
          loading={isLoading}
          showSorterTooltip={false}
          columns={columns}
          dataSource={dataSource}
          pagination={{
            total,
            current: searchParams.page,
            onChange: onPageChange,
            position: ["bottomCenter"],
            defaultPageSize: LIST_PAGE_SIZE,
            showSizeChanger: false,
          }}
        />
      </div>
    </div>
  );
};

export default UserSyncCardBalances;
